/** Caixa de notificações do membro: menções, atribuições, revisões. Clique abre a tarefa. */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { Digest, NotificacoesResposta, TipoNotificacao } from '@sgc/contracts';
import { get, post } from '../lib/api';
import { fmtData } from '../lib/formato';
import { fmtDataHora } from '../lib/tarefas';
import { Pill, Sec } from '../ui/layout';
import { useToast } from '../ui/toast';
import { BotaoAvisosEquipe } from '../lib/push-equipe';

export const Route = createFileRoute('/_app/notificacoes')({ component: Notificacoes });

const ROTULO: Record<TipoNotificacao, string> = { MENCAO: 'menção', ATRIBUIDA: 'atribuída a você', REVISAO: 'para revisar', DEVOLVIDA: 'devolvida', CLIENTE: 'cliente respondeu', CAIXA_POSTAL: 'caixa postal e-CAC' };
const COR: Record<TipoNotificacao, 'acento' | 'azul' | 'ambar' | 'verm' | 'verde'> = { MENCAO: 'acento', ATRIBUIDA: 'azul', REVISAO: 'ambar', DEVOLVIDA: 'verm', CLIENTE: 'verde', CAIXA_POSTAL: 'ambar' };

function Notificacoes() {
  const qc = useQueryClient();
  const toast = useToast();
  // lida sai da lista; 'mostrar lidas' traz de volta até alguém limpar
  const [verLidas, setVerLidas] = useState(false);
  const q = useQuery({ queryKey: ['notificacoes', verLidas ? 'todas' : 'abertas'], queryFn: () => get<NotificacoesResposta>('/v1/notificacoes', verLidas ? {} : { naoLidas: true }) });
  const digest = useQuery({ queryKey: ['digest'], queryFn: () => get<Digest>('/v1/notificacoes/digest') });
  const ler = useMutation({
    mutationFn: (b: { ids?: string[]; todas?: boolean }) => post('/v1/notificacoes/ler', b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notificacoes'] }),
  });
  const limpar = useMutation({
    mutationFn: () => post<{ removidas: number }>('/v1/notificacoes/limpar', {}),
    onSuccess: (x) => { void qc.invalidateQueries({ queryKey: ['notificacoes'] }); setVerLidas(false); toast.ok(`${x.removidas} notificação(ões) lida(s) apagada(s)`); },
    onError: (e) => toast.erro(e),
  });
  const r = q.data;
  const lidas = r?.lidas ?? 0;
  const d = digest.data;
  const linha = (t: Digest['atrasadas'][number]) => (
    <tr key={t.id}><td className="mono"><Link to="/tarefas" search={{ aba: 'lista', t: t.id }}>{fmtData(t.vencimento)}</Link></td><td>{t.clienteNome ?? <span className="dim">—</span>}</td><td><b>{t.titulo}</b> <span className="dim mono">{t.competenciaLabel}</span>{t.internoVencido && <> <Pill cor="ambar">⚠ interno {fmtData(t.prazoInterno ?? '')}</Pill></>}</td></tr>
  );
  return (
    <>
      <div className="topbar">
        <div><h1>Notificações</h1><div className="sub">{r ? `${r.naoLidas} não lida(s)${lidas ? ` · ${lidas} lida(s) guardada(s)` : ''}` : ''}</div></div>
        <div className="acoes">
          <BotaoAvisosEquipe />
          {lidas > 0 && <button className="btn" type="button" onClick={() => setVerLidas((v) => !v)}>{verLidas ? 'Ocultar lidas' : `Mostrar lidas (${lidas})`}</button>}
          {lidas > 0 && <button className="btn" type="button" disabled={limpar.isPending} onClick={() => { if (confirm(`Apagar ${lidas} notificação(ões) já lida(s)? As não lidas ficam.`)) limpar.mutate(); }}>Limpar lidas</button>}
          {r && r.naoLidas > 0 && <button className="btn" type="button" disabled={ler.isPending} onClick={() => ler.mutate({ todas: true })}>Marcar todas como lidas</button>}
        </div>
      </div>
      <div className="conteudo">
        <div className="painel-grid">
          <Sec titulo="Avisos" sub="menções, atribuições e revisões">
            <ul className="notifs">
              {r?.itens.length === 0 && <li className="dim">{lidas && !verLidas ? 'nada novo — as lidas saem da lista' : 'nada por aqui'}</li>}
              {r?.itens.map((n) => (
                <li key={n.id} className={n.lidaEm ? 'lida' : undefined}>
                  <Pill cor={COR[n.tipo]}>{ROTULO[n.tipo]}</Pill>
                  <div className="corpo">
                    {n.tarefaId ? <Link to="/tarefas" search={{ aba: 'lista', t: n.tarefaId }} onClick={() => { if (!n.lidaEm) ler.mutate({ ids: [n.id] }); }}><b>{n.titulo}</b></Link> : n.clienteId ? <Link to="/clientes/$id" params={{ id: n.clienteId }} onClick={() => { if (!n.lidaEm) ler.mutate({ ids: [n.id] }); }}><b>{n.titulo}</b></Link> : <b>{n.titulo}</b>}
                    <div>{n.texto}</div>
                    <div className="mini">{fmtDataHora(n.criadoEm)}</div>
                  </div>
                  {!n.lidaEm && <button className="btn link mini" type="button" onClick={() => ler.mutate({ ids: [n.id] })}>lida</button>}
                </li>
              ))}
            </ul>
          </Sec>
          <Sec titulo="Resumo do dia" sub="o que o e-mail das 7h traz — o que está com você">
            {d && (
              <>
                <div className="sec-info"><span><b style={{ color: 'var(--red)' }}>{d.totais.atrasadas}</b> atrasadas</span><span><b style={{ color: 'var(--amber)' }}>{d.totais.hoje}</b> hoje</span><span><b style={{ color: 'var(--blue)' }}>{d.totais.proximas}</b> próximos 3 dias</span>{(d.totais.internoVencido ?? 0) > 0 && <span><b style={{ color: 'var(--amber)' }}>{d.totais.internoVencido}</b> com prazo interno vencido</span>}{d.totais.semResponsavel > 0 && <span><b>{d.totais.semResponsavel}</b> sem responsável</span>}</div>
                {d.totais.atrasadas + d.totais.hoje + d.totais.proximas + (d.totais.internoVencido ?? 0) === 0 ? <div className="dim" style={{ marginTop: 10 }}>nada vencendo com você — o e-mail de hoje não seria enviado</div> : (
                  <div className="tbwrap" style={{ marginTop: 10 }}>
                    <table className="tb compacta">
                      <thead><tr><th>Vence</th><th>Cliente</th><th>Tarefa</th></tr></thead>
                      <tbody>{[...d.atrasadas, ...d.hoje, ...d.proximas, ...(d.internoVencido ?? [])].map(linha)}</tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </Sec>
        </div>
      </div>
    </>
  );
}
